import { useEffect, useState } from "react";
import { api } from "../api.js";
import { saveFile } from "./files.js";
import { isNative } from "./native.js";

/**
 * Attachment files (spec05) in the browser come with the session cookie. The app (spec08)
 * has a bearer token instead, which an <img> or a download link cannot send, so it fetches
 * the file with the token and uses a blob: URL.
 */
const blobUrls = new Map<string, Promise<string>>();

/** The src for an attachment image: its URL in the browser, a blob: URL in the app. */
export function attachmentSrc(url: string): Promise<string> {
  if (!isNative) return Promise.resolve(url);
  let src = blobUrls.get(url);
  if (!src) {
    src = api.attachments.file(url).then(async (res) => {
      if (!res.ok) throw new Error(`Attachment failed with status ${res.status}`);
      return URL.createObjectURL(await res.blob());
    });
    src.catch(() => blobUrls.delete(url));
    blobUrls.set(url, src);
  }
  return src;
}

/** Sets an <img> (built with DOM APIs) to show an attachment. */
export function showAttachment(img: HTMLImageElement, url: string) {
  if (!isNative) {
    img.src = url;
    return;
  }
  attachmentSrc(url).then(
    (src) => {
      img.src = src;
    },
    // Left without a src, the image shows as broken, like a missing file in the browser.
    () => undefined,
  );
}

export function useAttachmentSrc(url: string | undefined): string | undefined {
  const [src, setSrc] = useState(isNative ? undefined : url);
  useEffect(() => {
    if (!url) return setSrc(undefined);
    let current = true;
    attachmentSrc(url).then(
      (next) => current && setSrc(next),
      () => undefined,
    );
    return () => {
      current = false;
    };
  }, [url]);
  return src;
}

/** spec08 FR8: sign-out drops the files fetched in the app. */
export function clearAttachmentCache() {
  for (const src of blobUrls.values()) src.then(URL.revokeObjectURL, () => undefined);
  blobUrls.clear();
}

/** The file size, from a HEAD request with the client's credentials (null when missing). */
export async function attachmentSize(url: string): Promise<number | null> {
  try {
    const res = await api.attachments.file(url, { method: "HEAD" });
    return res.ok ? Number(res.headers.get("content-length")) : null;
  } catch {
    return null;
  }
}

/** The file name from `Content-Disposition` (RFC 6266, UTF-8 form preferred). */
export function fileNameOf(disposition: string | null): string | null {
  if (!disposition) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  if (utf8) {
    try {
      return decodeURIComponent(utf8);
    } catch {
      // Fall back to the ASCII name.
    }
  }
  return /filename="([^"]*)"/i.exec(disposition)?.[1] ?? null;
}

/** Downloads an attachment (the server sends it as `Content-Disposition: attachment`). */
export async function downloadAttachment(url: string, name: string) {
  if (!isNative) {
    const a = document.createElement("a");
    a.href = url;
    a.download = "";
    document.body.append(a);
    a.click();
    a.remove();
    return;
  }
  const res = await api.attachments.file(url);
  if (!res.ok) throw new Error(`Download failed with status ${res.status}`);
  await saveFile(await res.blob(), fileNameOf(res.headers.get("content-disposition")) ?? name);
}
